import React from 'react';
import {
  AlertTriangle,
  Check,
  CircleCheck,
  CircleX,
  Heart,
  ImageOff,
  Star,
} from 'lucide-react';
import './ProductDetailCard.css';

const iconSm = { size: 18, strokeWidth: 1.75 };
const featureCheckProps = { size: 14, strokeWidth: 2.5 };
const imagePlaceholderProps = { size: 64, strokeWidth: 1.5 };

const ProductDetailCard = ({ product, onAddToCart }) => {
    if (!product) return null;
    const images = product.images && product.images.length > 0 ? product.images : [product.image || "/images/products/bag1.jpg"];

    // Stock validation
    const stock = product.stock || 0;
    const moq = product.moq || 100;
    const isOutOfStock = stock === 0;
    const insufficientStock = stock < moq;
    const canAddToCart = !isOutOfStock && !insufficientStock;

    // Use product data for all fields
    return (
        <div className="pd-product-card">
            <div className="pd-product-images">
                <div className="pd-main-image" id="mainImage">
                    {images[0] ? (
                        <img src={images[0]} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                        <span className="pd-image-placeholder" aria-hidden>
                            <ImageOff {...imagePlaceholderProps} />
                        </span>
                    )}
                </div>
                <div className="pd-thumbnail-images">
                    {images.map((img, idx) => (
                        <div className={`pd-thumbnail${idx === 0 ? ' active' : ''}`} key={idx} data-image={img}>
                            {img.startsWith('/') || img.startsWith('http') ? <img src={img} alt="thumb" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : img}
                        </div>
                    ))}
                </div>
            </div>

            <div className="pd-product-info">
                <div className="pd-product-category">{product.category || 'Paper Bags / Kraft Bags'}</div>
                <h1 className="pd-product-name">{product.name}</h1>

                <div className="pd-product-rating">
                    <div className="pd-stars" aria-hidden>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} size={16} strokeWidth={0} fill="#fbbf24" color="#fbbf24" />
                        ))}
                    </div>
                    <span className="pd-rating-text">{product.rating ? `${product.rating} (${product.reviews || 0} reviews)` : '4.8 (234 reviews)'}</span>
                </div>

                <div className="pd-product-price">
                    <span className="pd-current-price">{product.price?.currency || '₹'} {product.price?.amount || '0.00'}</span>
                    {product.price?.original && (
                        <span className="pd-original-price">{product.price.currency || '₹'} {product.price.original}</span>
                    )}
                    {product.price?.discount && (
                        <span className="pd-discount-badge">{product.price.discount}% OFF</span>
                    )}
                </div>

                <div className={`pd-stock-status ${product.stock === 0 ? 'pd-out-of-stock' : (product.stock < (product.moq || 100) ? 'pd-low-stock' : 'pd-in-stock')}`}>
                    <div className="pd-stock-indicator"></div>
                    <span className="pd-stock-text">
                        {product.stock === 0 ? (
                            <>
                                <CircleX {...iconSm} className="pd-stock-lead-icon" aria-hidden />
                                Out of Stock
                            </>
                        ) : product.stock < (product.moq || 100) ? (
                            <>
                                <AlertTriangle {...iconSm} className="pd-stock-lead-icon" aria-hidden />
                                Only {product.stock} units left (MOQ: {product.moq || 100})
                            </>
                        ) : (
                            <>
                                <CircleCheck {...iconSm} className="pd-stock-lead-icon" aria-hidden />
                                In Stock - {product.stock} units available
                            </>
                        )}
                    </span>
                </div>

                <p className="pd-product-description">
                    {product.description || 'Our premium kraft paper bags are made from 100% recycled materials, offering exceptional durability and eco-friendliness. Perfect for retail stores, restaurants, and events. These bags feature reinforced handles and superior tear resistance.'}
                </p>

                <div className="pd-specifications">
                    <h3 className="pd-spec-title">Product Specifications</h3>
                    <div className="pd-spec-grid">
                        {product.bagSize?.cm && (
                            <div className="pd-spec-item">
                                <div className="pd-spec-value">{product.bagSize.cm}</div>
                                <div className="pd-spec-label">Size (L×W×H)</div>
                            </div>
                        )}
                        {product.specifications?.gsm && (
                            <div className="pd-spec-item">
                                <div className="pd-spec-value">{product.specifications.gsm} GSM</div>
                                <div className="pd-spec-label">Paper Weight</div>
                            </div>
                        )}
                        {product.specifications?.bf && (
                            <div className="pd-spec-item">
                                <div className="pd-spec-value">{product.specifications.bf} BF</div>
                                <div className="pd-spec-label">Burst Factor</div>
                            </div>
                        )}
                        {product.material && (
                            <div className="pd-spec-item">
                                <div className="pd-spec-value">{product.material}</div>
                                <div className="pd-spec-label">Material</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="pd-features">
                    <h3 className="pd-features-title">Key Features</h3>
                    <div className="pd-features-list">
                        {product.features && product.features.length > 0 ? (
                            product.features.map((f, i) => (
                                <div className="pd-feature-item" key={i}>
                                    <span className="pd-feature-icon" aria-hidden>
                                        <Check {...featureCheckProps} />
                                    </span>
                                    {f}
                                </div>
                            ))
                        ) : (
                            <>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>100% Recyclable Material</div>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>Reinforced Twisted Handles</div>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>Food Grade Safe</div>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>Tear Resistant Design</div>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>Biodegradable & Compostable</div>
                                <div className="pd-feature-item"><span className="pd-feature-icon" aria-hidden><Check {...featureCheckProps} /></span>Custom Printing Available</div>
                            </>
                        )}
                    </div>
                </div>

                <div className="pd-product-actions">
                    <button
                        className="pd-btn-primary"
                        onClick={onAddToCart}
                        disabled={!canAddToCart}
                        title={
                            isOutOfStock ? "Out of stock" :
                            insufficientStock ? `Insufficient stock (MOQ: ${moq}, Available: ${stock})` :
                            "Add to cart"
                        }
                    >
                        {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                    </button>
                    <button
                        className="pd-btn-secondary"
                        disabled={!canAddToCart}
                    >
                        Buy Now
                    </button>
                    <button type="button" className="pd-btn-icon" aria-label="Add to wishlist">
                        <Heart {...iconSm} aria-hidden />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailCard;
